/**
 * LocationMap Component
 *
 * Map showing approximate property location using CartoDB (free, English labels).
 */

import React, { useEffect, useRef } from 'react';
import type { ListingLocation } from '../hooks/useListing';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface LocationMapProps {
  location: ListingLocation;
}

export const LocationMap: React.FC<LocationMapProps> = ({ location }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  // Use listing coordinates (from WordPress database)
  const lat = location.coordinates?.latitude || 33.8938;
  const lng = location.coordinates?.longitude || 35.5018;
  const hasValidCoords = location.coordinates?.latitude && location.coordinates?.longitude;

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize the map centered on listing coordinates
    const map = L.map(mapRef.current, {
      center: [lat, lng],
      zoom: hasValidCoords ? 14 : 10,
      scrollWheelZoom: false,
      attributionControl: true,
    });

    // CartoDB Positron - clean English map tiles (free, no API key)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      maxZoom: 19,
      subdomains: 'abcd',
    }).addTo(map);

    // Add a circle to show approximate location (privacy)
    L.circle([lat, lng], {
      color: '#10b981',
      fillColor: '#10b981',
      fillOpacity: 0.2,
      radius: 500, // 500 meters radius for privacy
    }).addTo(map);

    // Add a marker at center
    const customIcon = L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          width: 40px;
          height: 40px;
          background: #10b981;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    L.marker([lat, lng], { icon: customIcon }).addTo(map);

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [lat, lng]);

  return (
    <section className="py-6 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Where you'll be
      </h2>

      {/* OpenStreetMap */}
      <div
        ref={mapRef}
        className="aspect-[16/9] md:aspect-[2/1] rounded-xl overflow-hidden relative z-0"
        style={{ minHeight: '300px', isolation: 'isolate' }}
      />

      {/* Location Info */}
      <div className="mt-4">
        <h3 className="font-medium text-gray-900">
          {location.city}, {location.area}
        </h3>
        <p className="text-gray-600 mt-2">
          {location.city} is located in {location.area}, {location.country}.
          The exact location will be provided after you complete your booking.
        </p>
      </div>

      {/* Getting Around */}
      <div className="mt-6">
        <h3 className="font-medium text-gray-900 mb-3">Getting around</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <CarIcon className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Car recommended</p>
              <p className="text-gray-500">Best way to explore the area</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <ParkingIcon className="w-5 h-5 text-gray-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Free parking</p>
              <p className="text-gray-500">Available on premises</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Icons
const CarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const ParkingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2H5z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7h4a3 3 0 010 6H9" />
  </svg>
);

export default LocationMap;
